// src/components/RefundForm.js
// The refund fields, shared by the « new refund » modal (Refunds page), the
// ticket's Refunds tab (TicketRefunds.js, in the ticket window's side panel)
// and the refund detail. Controlled: the parent owns the form object
// (utils/refunds.js builds and serialises it); props and export are a
// contract with those three parents. The ticket's declared values are offered
// as one-click fills, never applied silently: a declared value is what the
// parcel was worth, not what the carrier granted. The grid fits its container
// (a 360 px side panel or a wide card), never the viewport.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '../design/ui';
import {
  REFUND_REASONS,
  REFUND_SETTLEMENTS,
  REFUND_SOURCES,
  REFUND_STATUSES,
  formatRefundMoney,
} from '../utils/refunds';
import { formatAmount } from './refundsMoney';
import './Refunds.css';

const AMOUNT_FIELDS = ['shipping_cost_refund_amount', 'content_refund_amount', 'insurance_refund_amount'];

const RefundForm = ({ form, onChange, declared, ticketOrder, idPrefix = 'rfm' }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const set = (field) => (event) => onChange(field, event.target.value);
  const id = (name) => `${idPrefix}-${name}`;

  // Keep a legacy value selectable so an existing refund never silently changes.
  const withCurrent = (values, current) => (current && !values.includes(current) ? [...values, current] : values);

  const suggestions = [
    { field: 'shipping_cost_refund_amount', value: declared?.shipping_price, label: t('refunds.useDeclaredShipping', 'Frais de port {{amount}}', { amount: formatRefundMoney(declared?.shipping_price, declared?.currency, lang) }) },
    { field: 'content_refund_amount', value: declared?.goods_value, label: t('refunds.useDeclaredGoods', 'Valeur marchandise {{amount}}', { amount: formatRefundMoney(declared?.goods_value, declared?.currency, lang) }) },
    { field: 'insurance_refund_amount', value: declared?.insurance_value, label: t('refunds.useDeclaredInsurance', 'Assurance {{amount}}', { amount: formatRefundMoney(declared?.insurance_value, declared?.currency, lang) }) },
  ].filter((s) => Number(s.value || 0) > 0 && String(form[s.field] || '') !== String(s.value));

  const total = AMOUNT_FIELDS.reduce((sum, field) => sum + (Number(form[field]) || 0), 0);

  const select = (name, label, values, labelOf) => (
    <div className="u-field">
      <label className="u-label" htmlFor={id(name)}>{label}</label>
      <select id={id(name)} className="u-select" value={form[name]} onChange={set(name)}>
        {withCurrent(values, form[name]).map((value) => (
          <option key={value} value={value}>{labelOf(value)}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="rfm-form refund-form-v2">
      <section className="rfm-section refund-form-v2__section">
        <h3 className="u-overline refund-form-v2__title">{t('refunds.sectionDecision', 'Décision')}</h3>
        <div className="refund-form-v2__grid">
          {select('status', t('refunds.status', 'Statut'), REFUND_STATUSES, (value) => t(`refunds.statuses.${value}`, value))}
          {select('source', t('refunds.source', 'Source'), REFUND_SOURCES, (value) => t(`refunds.sources.${value}`, value))}
          {select('reason', t('refunds.reason', 'Motif'), REFUND_REASONS, (value) => t(`refunds.reasons.${value}`, value))}
          {select('settlement', t('refunds.settlement', 'Règlement'), REFUND_SETTLEMENTS, (value) => t(`refunds.settlements.${value}`, value))}
        </div>
      </section>

      <section className="rfm-section refund-form-v2__section">
        <div className="refund-form-v2__title-row">
          <h3 className="u-overline refund-form-v2__title">{t('refunds.sectionAmounts', 'Montants')}</h3>
          {total > 0 && (
            <span className="refund-form-v2__total">
              {t('ordersMoneyUi.form.total', 'Total {{amount}}', { amount: formatAmount(total, form.currency || 'EUR', lang) })}
            </span>
          )}
        </div>
        <div className="refund-form-v2__grid">
          {[
            ['shipping_cost_refund_amount', t('refunds.shippingAmount', 'Transport')],
            ['content_refund_amount', t('refunds.contentAmount', 'Contenu')],
            ['insurance_refund_amount', t('refunds.insuranceAmount', 'Assurance')],
          ].map(([field, label]) => (
            <div key={field} className="u-field">
              <label className="u-label" htmlFor={id(field)}>{label}</label>
              <input id={id(field)} className="u-input refund-form-v2__amount" type="number" min="0" step="0.01" inputMode="decimal" placeholder="—"
                value={form[field]} onChange={set(field)} />
            </div>
          ))}
          <div className="u-field">
            <label className="u-label" htmlFor={id('currency')}>{t('refunds.currency', 'Devise')}</label>
            <input id={id('currency')} className="u-input" type="text" maxLength={3} value={form.currency}
              onChange={(e) => onChange('currency', e.target.value.toUpperCase())} />
          </div>
        </div>
        {suggestions.length > 0 && (
          <div className="refund-form-v2__suggestions">
            <span>{t('refunds.useDeclaredHint', 'Déclaré sur le ticket :')}</span>
            {suggestions.map((s) => (
              <button key={s.field} type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => onChange(s.field, String(s.value))}>
                {s.label}
              </button>
            ))}
          </div>
        )}
        <p className="u-help">
          {t('refunds.amountsHint', 'Laissez vide tant que le transporteur n’a pas donné le montant : le remboursement affiche « montant à confirmer ».')}
        </p>
      </section>

      <section className="rfm-section refund-form-v2__section">
        <h3 className="u-overline refund-form-v2__title">{t('refunds.sectionReferences', 'Références')}</h3>
        <div className="refund-form-v2__grid refund-form-v2__grid--wide">
          <div className="u-field">
            <label className="u-label" htmlFor={id('carrier_reference')}>{t('refunds.carrierReference', 'Réf. dossier transporteur')}</label>
            <input id={id('carrier_reference')} className="u-input" type="text" maxLength={80} value={form.carrier_reference}
              onChange={set('carrier_reference')} />
          </div>
          <div className="u-field">
            <label className="u-label" htmlFor={id('order_reference')}>{t('refunds.orderReference', 'Commande')}</label>
            <input id={id('order_reference')} className="u-input" type="text" maxLength={100} value={form.order_reference}
              placeholder={ticketOrder ? t('refunds.orderFromTicket', 'Du ticket : {{order}}', { order: ticketOrder }) : t('refunds.orderPlaceholder', 'Référence de commande')}
              onChange={set('order_reference')} />
          </div>
        </div>
        <div className="refund-form-v2__switch">
          <Switch
            id={id('has_insurance')}
            checked={Boolean(form.has_insurance)}
            onChange={(checked) => onChange('has_insurance', checked)}
            label={t('refunds.hasInsurance', 'Colis assuré')}
          />
        </div>
        {form.has_insurance && (
          <div className="refund-form-v2__grid refund-form-v2__grid--wide">
            <div className="u-field">
              <label className="u-label" htmlFor={id('insurance_value')}>{t('refunds.insuranceValue', 'Valeur assurée')}</label>
              <input id={id('insurance_value')} className="u-input" type="number" min="0" step="0.01" value={form.insurance_value}
                onChange={set('insurance_value')} />
            </div>
            <div className="u-field">
              <label className="u-label" htmlFor={id('insurance_price')}>{t('refunds.insurancePrice', 'Prix de l’assurance')}</label>
              <input id={id('insurance_price')} className="u-input" type="number" min="0" step="0.01" value={form.insurance_price}
                onChange={set('insurance_price')} />
            </div>
          </div>
        )}
        <div className="u-field refund-form-v2__note">
          <label className="u-label" htmlFor={id('note')}>{t('refunds.note', 'Note')}</label>
          <textarea id={id('note')} className="u-textarea" rows={3} maxLength={2000} value={form.note}
            placeholder={t('refunds.notePlaceholder', 'Note interne sur ce remboursement')} onChange={set('note')} />
        </div>
      </section>
    </div>
  );
};

export default RefundForm;
